import {
  EllipsisOutlined,
  QuestionCircleOutlined,
  SearchOutlined
} from '@ant-design/icons'
import {
  Button,
  Col,
  Dropdown,
  message,
  Modal,
  Row,
  Space,
  Table,
  Input
} from 'antd'
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '~/components/PageHeader'
import { adminApi } from '~/api'
import { apiDeleteRegistration } from '~/api/adminApi'
import { type Registration } from '~/types/course'
import {
  compareNumber,
  compareText,
  countryCodeToName,
  showErrors
} from '~/utils/helpers'
import { useWaitlistsQuery } from './query'

const { confirm } = Modal

export default function WaitlistList({ course }: { course: any }) {
  const [filterInput, setFilterInput] = useState('')
  const { data: waitlists, isLoading } = useWaitlistsQuery({
    course_id: course.id,
    search: filterInput,
    per_page: 1000
  })

  const queryClient = useQueryClient()
  const confirmWaitlistMutation = useMutation({
    mutationFn: async (id: number) => {
      await adminApi.confirmWaitlist(id)
    },
    onError: showErrors,
    onSuccess: async () => {
      message.success('Converted to registration successfully')
      await queryClient.invalidateQueries({ queryKey: ['waitlists'] })
      await queryClient.invalidateQueries({ queryKey: ['courses', course.id] })
    }
  })

  const deleteWaitlistMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiDeleteRegistration(id)
    },
    onError: showErrors,
    onSuccess: async () => {
      message.success('Delete successfully')
      await queryClient.invalidateQueries({ queryKey: ['waitlists'] })
      await queryClient.invalidateQueries({ queryKey: ['courses', course.id] })
    }
  })

  const handleConfirmWaitlist = (item: Registration): void => {
    confirm({
      title: 'Confirm',
      icon: <QuestionCircleOutlined />,
      content: `Do you want to convert the registration with email ${item.email} to a confirmed registration? This will also create a workstation.`,
      okText: 'YES',
      okType: 'primary',
      cancelText: 'NO',
      onOk: () => {
        confirmWaitlistMutation.mutate(item.id)
      }
    })
  }

  const handleDeleteWaitlist = (item: Registration): void => {
    confirm({
      title: 'Confirm',
      icon: <QuestionCircleOutlined />,
      content: `Do you want to delete the waitlist registration with email ${item.email}?`,
      okText: 'YES',
      okType: 'danger',
      cancelText: 'NO',
      onOk: () => {
        deleteWaitlistMutation.mutate(item.id)
      }
    })
  }

  return (
    <>
      <PageHeader
        className='radup-page-header'
        extra={[
          <Col key={1}>
            <Row>
              <Space>
                <Input.Search
                  placeholder='Search'
                  allowClear
                  enterButton={<SearchOutlined />}
                  onSearch={setFilterInput}
                />
              </Space>
            </Row>
          </Col>
        ]}
      />

      <Table
        size='small'
        tableLayout='auto'
        dataSource={waitlists}
        loading={isLoading}
        rowKey="id"
        footer={() => (
          <Row justify='start'>
            <span>Total: {waitlists?.length ?? 0} participants</span>
          </Row>
        )}
        columns={[
          {
            title: 'ID',
            dataIndex: 'id',
            sorter: (a, b) => compareNumber(a.id, b.id)
          },
          {
            title: 'Name',
            render: (v, student) =>
              [student?.first_name, student?.last_name].join(' ')
          },
          {
            title: 'Email',
            render: (student) => student?.email,
            sorter: (a, b) => compareText(a.email, b.email)
          },
          {
            title: 'Type',
            dataIndex: 'join_type'
          },
          {
            title: 'Country',
            render: (item) => countryCodeToName(item.country_code)
          },
          {
            title: 'Institution',
            render: (item) => item.institution
          },
          {
            title: 'Actions',
            render: (item) => {
              return (
                <Space size='middle'>
                  <Dropdown
                    key={2}
                    className='dropdown-action'
                    placement='bottom'
                    trigger={['click']}
                    menu={{
                      items: [
                        {
                          label: 'Convert to registration',
                          key: 'convert',
                          onClick: () => {
                            handleConfirmWaitlist(item)
                          }
                        },
                        {
                          label: 'Delete',
                          key: 'delete',
                          danger: true,
                          onClick: () => {
                            handleDeleteWaitlist(item)
                          }
                        }
                      ]
                    }}
                  >
                    <Button type='primary' icon={<EllipsisOutlined />} />
                  </Dropdown>
                </Space>
              )
            }
          }
        ]}
      />
    </>
  )
}
